
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Hero from '../home/components/Hero';
import Footer from '../home/components/Footer';

interface FishStock {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  price: number;
}

export default function Supplier() {
  const { t, i18n } = useTranslation('common');
  const [stocks, setStocks] = useState<FishStock[]>([]);
  const [supplierInfo, setSupplierInfo] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    quantity: '',
    price: ''
  });

  const fishTypes = [
    'Bar Frais',
    'Bossus Frais',
    'Brochet',
    'Capitaine',
    'Carpe Fraîche',
    'Crabes',
    'Crevettes roses',
    'Daurade',
    'Gambas',
    'Langoustes',
    'Machoiron Frais',
    'Packs',
    'Sole Fraîche'
  ];

  const addStock = () => {
    if (!formData.name || !formData.quantity || !formData.price) {
      alert(t('supplier.fillAllFields'));
      return;
    }

    const newStock: FishStock = {
      id: Date.now().toString(),
      name: formData.name,
      quantity: parseFloat(formData.quantity),
      unit: 'kg',
      price: parseFloat(formData.price)
    };

    setStocks([...stocks, newStock]);
    setFormData({ name: '', quantity: '', price: '' });
  };

  const removeStock = (id: string) => {
    setStocks(stocks.filter(stock => stock.id !== id));
  };

  const sendToWhatsApp = () => {
    if (stocks.length === 0) {
      alert(t('supplier.addAtLeastOne'));
      return;
    }

    if (!supplierInfo.firstName || !supplierInfo.lastName || !supplierInfo.phone) {
      alert(t('supplier.fillPersonalInfo'));
      return;
    }

    let message = `🐟 *${t('whatsapp.stockTitle')}*\n\n`;
    message += `📅 ${t('whatsapp.date')}: ${new Date().toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}\n`;
    message += `👤 ${t('whatsapp.supplier')}: ${supplierInfo.firstName} ${supplierInfo.lastName}\n`;
    message += `📞 ${t('whatsapp.phone')}: ${supplierInfo.phone}\n\n`;
    
    stocks.forEach((stock, index) => {
      message += `${index + 1}. *${stock.name}*\n`;
      message += `   ${t('whatsapp.quantity')}: ${stock.quantity} ${stock.unit}\n`;
      message += `   ${t('whatsapp.unitPrice')}: ${stock.price.toLocaleString()} FCFA/${stock.unit}\n\n`;
    });

    const totalQuantity = stocks.reduce((sum, stock) => sum + stock.quantity, 0);
    message += `📊 *${t('whatsapp.totalAvailable')}: ${totalQuantity} ${t('whatsapp.kgAvailable')}*\n\n`;
    message += `⚠️ *${t('whatsapp.priceNote')}*\n\n`;
    message += t('whatsapp.sentBySupplier');
    
    const whatsappUrl = `https://wa.me/237654391120?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const clearAll = () => {
    if (confirm(t('supplier.confirmClear'))) {
      setStocks([]);
    }
  };

  // Scroll to main content when page loads
  useEffect(() => {
    const timer = setTimeout(() => {
      const mainContent = document.querySelector('.supplier-main-content');
      if (mainContent) {
        mainContent.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-teal-50/30">
      <Hero cartCount={0} onCartClick={() => {}} />
      
      <div className="supplier-main-content max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            {t('supplier.title')}
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            {t('supplier.description')}
          </p>
        </div>

        {/* Informations du fournisseur */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
            <i className="ri-user-line text-teal-600"></i>
            {t('supplier.supplierInfo')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                {t('supplier.firstName')} *
              </label>
              <input
                type="text"
                value={supplierInfo.firstName}
                onChange={(e) => setSupplierInfo({ ...supplierInfo, firstName: e.target.value })}
                placeholder={t('supplier.firstName')}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                {t('supplier.lastName')} *
              </label>
              <input
                type="text"
                value={supplierInfo.lastName}
                onChange={(e) => setSupplierInfo({ ...supplierInfo, lastName: e.target.value })}
                placeholder={t('supplier.lastName')}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                {t('supplier.phone')} *
              </label>
              <input
                type="tel"
                value={supplierInfo.phone}
                onChange={(e) => setSupplierInfo({ ...supplierInfo, phone: e.target.value })}
                placeholder="Ex: +237 6XX XXX XXX"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
              <i className="ri-add-circle-line text-teal-600"></i>
              {t('supplier.addFish')}
            </h2>

            <div className="space-y-4 sm:space-y-5">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  {t('supplier.fishType')}
                </label>
                <div className="relative">
                  <select
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-8 text-sm sm:text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 bg-white cursor-pointer"
                  >
                    <option value="">{t('supplier.selectFish')}</option>
                    {fishTypes.map(fish => (
                      <option key={fish} value={fish}>{fish}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  {t('supplier.quantity')}
                </label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="Ex: 25"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  {t('supplier.price')}
                </label>
                <input
                  type="number"
                  step="100"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="Ex: 3500"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={addStock}
                className="w-full bg-teal-600 text-white py-2.5 sm:py-3 md:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line text-lg sm:text-xl"></i>
                {t('supplier.addToStock')}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <i className="ri-list-check text-teal-600"></i>
                {t('supplier.dailyStock')}
              </h2>
              {stocks.length > 0 && (
                <button
                  onClick={clearAll}
                  className="text-red-600 hover:text-red-700 text-xs sm:text-sm font-semibold cursor-pointer whitespace-nowrap"
                >
                  {t('supplier.clearAll')}
                </button>
              )}
            </div>

            {stocks.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-inbox-line text-5xl sm:text-6xl text-gray-300 mb-4"></i>
                <p className="text-sm sm:text-base text-gray-500">{t('supplier.noFish')}</p>
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                  {stocks.map((stock) => (
                    <div key={stock.id} className="flex items-center justify-between bg-teal-50 rounded-xl p-3 sm:p-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base truncate">{stock.name}</h3>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <i className="ri-scales-line"></i>
                            {stock.quantity} kg
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-money-dollar-circle-line"></i>
                            {stock.price.toLocaleString()} FCFA/kg
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeStock(stock.id)}
                        className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full hover:bg-red-100 transition-colors cursor-pointer flex-shrink-0 ml-2"
                      >
                        <i className="ri-delete-bin-line text-red-500 text-sm sm:text-base"></i>
                      </button>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 mb-4">
                  <div className="flex justify-between items-center text-base sm:text-lg font-bold">
                    <span className="text-gray-700">{t('supplier.totalAvailable')}</span>
                    <span className="text-teal-600">
                      {stocks.reduce((sum, stock) => sum + stock.quantity, 0)} kg
                    </span>
                  </div>
                </div>

                <button
                  onClick={sendToWhatsApp}
                  className="w-full bg-green-600 text-white py-2.5 sm:py-3 md:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
                  {t('supplier.sendWhatsApp')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
